"use client";
import { useState } from "react";
import ISwiper from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade, Pagination, FreeMode, Navigation, Thumbs } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Box, Button, useMediaQuery } from "@mui/material";
import Image from "next/image";
import ReactPlayer from "react-player";
import ArrowLIcon from "@mui/icons-material/KeyboardArrowLeft";

interface Images {
  name?: string | null;
  href?: string | null;
  type?: string;
}

export const SwiperComponent: React.FC<{
  images?: Images[] | null;
  isFacility?: boolean;
  isItineraryMobile?: boolean;
}> = ({ images, isFacility, isItineraryMobile }) => {
  const [swiperRef, setSwiperRef] = useState<ISwiper | null>();
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
  const [thumbsSwiper, setThumbsSwiper] = useState<ISwiper | null>(null);
  const isMobile = useMediaQuery("(max-width: 600px)");

  const imageLength = isMobile ? 4 : 7;
  return (
    <Box sx={{ position: "relative" }}>
      {/* Main Swiper */}
      <Box
        sx={{
          position: "relative",
          width: { xs: "calc(100% + 2rem)", sm: "100%", md: "100%" },
          height: { xs: "200px", sm: "320px" },
          mb: "10px",
          ml: { xs: "-1rem", sm: "0rem" },
          minHeight: "200px",
          ...(isItineraryMobile && {
            width: { xs: "calc(100%)", sm: "100%", md: "100%" },
            ml: { xs: "0rem", sm: "0rem" },
            border: "2px solid #C6A7FF",
            borderBottom: "0px solid #C6A7FF",
            borderRadius: "10px 10px 0 0",
            overflow: "hidden",
            mb: "0px",
          }),
        }}
      >
        {(images?.length ?? 0) > 1 && (
          <Button
            size="small"
            variant="contained"
            disableElevation
            aria-label="Previous image"
            sx={{
              borderRadius: { xs: "0px 30px 30px 0px", sm: "30px" },
              cursor: "pointer",
              zIndex: 3,
              minWidth: "unset",
              position: "absolute",
              p: { xs: "16px 4px 16px 0px", sm: "unset" },
              top: "50%",
              left: { xs: "0rem", sm: "1rem" },
              transform: "translateY(-50%)",
              svg: {
                m: { xs: "2px 2px", sm: "4px 4px" },
                color: "#fff",
                fontSize: { xs: "25px" },
              },
              ...(isItineraryMobile && {
                borderRadius: { xs: "0 10px 10px 0", sm: "30px" },
                p: { xs: "10px 2px 10px 0px", sm: "unset" },
                left: { xs: "0px", sm: "1rem" },
              }),
            }}
            onClick={() => swiperRef?.slidePrev()}
          >
            <ArrowLIcon />
          </Button>
        )}

        <Swiper
          onSwiper={setSwiperRef}
          effect={"fade"}
          pagination={{
            clickable: true,
            type: "custom",
          }}
          modules={[Autoplay, EffectFade, Pagination, Navigation, Thumbs]}
          thumbs={{
            swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
          }}
          autoplay={
            isItineraryMobile
              ? false
              : {
                  delay: 1500,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: false,
                }
          }
          className="floor_map"
          style={{
            height: "100%",
            width: "100%",
            boxShadow:
              "rgb(27 127 204 / 26%) 0px 10px 36px 0px, rgba(255, 255, 255, .2) 0px 0px 0px 1px",
            borderRadius: !isMobile ? "15px" : "0px",
          }}
        >
          {images?.map((item, index) => (
            <SwiperSlide
              key={index + 1}
              data-swiper-autoplay="2000"
              onMouseEnter={() => {
                if (!isItineraryMobile) {
                  setHoveredIndex(index);
                  if (swiperRef) swiperRef.autoplay.start();
                }
              }}
              onMouseLeave={() => {
                if (!isItineraryMobile) {
                  setHoveredIndex(-1);
                  if (swiperRef) swiperRef?.autoplay?.stop();
                }
              }}
              className={hoveredIndex == index ? "floor_map_image_zoom" : ""}
            >
              {item?.type == "image" ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    position: "relative",
                  }}
                >
                  <Image
                    src={item?.href ?? ""}
                    alt={item?.name ?? ""}
                    fill
                    style={{ objectFit: "cover" }}
                    loader={({ src }) => src}
                    unoptimized
                    blurDataURL={item?.href ?? ""}
                    placeholder="blur"
                    priority
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    position: "relative",
                    width: { xs: "100%", sm: "100%" },
                    height: { xs: "200px", sm: "400px" },
                    m: { xs: "0px 0px 16px 0px", sm: "0px 16px 0px 0px" },
                    float: { xs: "top", sm: "left" },
                    iframe: {
                      boxShadow:
                        "rgb(27 127 204 / 26%) 0px 10px 36px 0px, rgba(255, 255, 255, .2) 0px 0px 0px 1px",
                      borderRadius: { sm: "15px" },
                    },
                  }}
                >
                  <ReactPlayer
                    url={item?.href ?? ""}
                    width="100%"
                    height="100%"
                    config={{
                      youtube: {
                        playerVars: {
                          modestbranding: 1,
                          autoPlay: 1,
                          rel: 0,
                          fs: 0,
                        },
                      },
                    }}
                    controls={true}
                    playIcon={<></>}
                    muted
                    loop={true}
                    volume={0}
                  />
                </Box>
              )}
            </SwiperSlide>
          ))}
        </Swiper>

        {(images?.length ?? 0) > 1 && (
          <Button
            size="small"
            disableElevation
            variant="contained"
            aria-label="Next image"
            onClick={() => swiperRef?.slideNext()}
            sx={{
              borderRadius: { xs: "30px 0px 0px 30px", sm: "30px" },
              cursor: "pointer",
              zIndex: 3,
              minWidth: "unset",
              position: "absolute",
              p: { xs: "16px 0px 16px 4px", sm: "unset" },
              top: "50%",
              right: { xs: "0rem", sm: "1rem" },
              transform: "translateY(-50%)",
              svg: {
                m: { xs: "2px 2px", sm: "4px 4px" },
                color: "#fff",
                fontSize: { xs: "25px" },
                rotate: "180deg",
              },
              ...(isItineraryMobile && {
                borderRadius: { xs: "10px 0px 0px 10px", sm: "30px" },
                p: { xs: "10px 0px 10px 2px", sm: "unset" },
                right: { xs: "0px", sm: "1rem" },
              }),
            }}
          >
            <ArrowLIcon />
          </Button>
        )}
      </Box>

      {/* Thums Swiper */}
      {!isFacility && (images?.length ?? 0) > 1 && (
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            width: { xs: "calc(100% + 2rem)", sm: "100%", md: "100%" },
            ml: { xs: "-1rem", sm: "0rem" },
            height: "fit-content",
            zIndex: 1,
            bgcolor: "#838486",
            backdropFilter: "blur(100px)",
            borderRadius: { sm: "6px" },
            overflow: "hidden",
            p: "6px",
            ".swiper-slide.swiper-slide-visible img, .swiper-slide.swiper-slide-visible iframe": {
              border: "none !important",
              borderRadius: { sm: "6px !important" },
            },
            ".swiper-slide.swiper-slide-thumb-active img, .swiper-slide.swiper-slide-thumb-active iframe":
              {
                border: "2px solid #fff !important",
                borderRadius: { sm: "6px !important" },
              },

            ".swiper-wrapper": {
              pl: "6px",
            },
            ".swiper": {
              height: "45px",
              borderRadius: { sm: "8px" },
            },
            ...(isItineraryMobile && {
              width: { xs: "calc(100%)", sm: "100%", md: "100%" },
              ml: { xs: "0rem", sm: "0rem" },
              border: "2px solid #C6A7FF",
              borderRadius: "0 0 10px 10px",
              bgcolor: "#C6A7FF",
              ".swiper-wrapper": {
                pl: "1px",
              },
              ".swiper-slide.swiper-slide-visible img, .swiper-slide.swiper-slide-visible iframe": {
                border: "2px solid #5D04D9 !important",
                borderRadius: { sm: "6px !important" },
              },
              ".swiper-slide.swiper-slide-thumb-active img, .swiper-slide.swiper-slide-thumb-active iframe":
                {
                  border: "2px solid #fff !important",
                  borderRadius: { sm: "6px !important" },
                  boxShadow: "0px 0px 4px #000",
                },
            }),
          }}
        >
          {(images?.length ?? 0) > imageLength && (
            <Button
              size="small"
              variant="contained"
              disableElevation
              aria-label="Previous thumbnails"
              sx={{
                width: "25px",
                height: "50px",
                borderRadius: "6px",
                cursor: "pointer",
                zIndex: 1,
                minWidth: "unset",
                svg: {
                  m: { xs: "2px 2px", sm: "4px 4px" },
                  color: "#fff",
                  fontSize: { xs: "25px" },
                },
              }}
              onClick={() => thumbsSwiper?.slidePrev()}
            >
              <ArrowLIcon />
            </Button>
          )}

          <Swiper
            onSwiper={setThumbsSwiper}
            spaceBetween={8}
            slidesPerView={
              isMobile
                ? (images?.length ?? 0) > 4
                  ? 4.5
                  : images?.length
                : (images?.length ?? 0) > 7
                ? 7.5
                : images?.length
            }
            freeMode={true}
            watchSlidesProgress={true}
            modules={[FreeMode, Navigation, Thumbs]}
            style={{
              margin: "0px 6px 0px 0px",
              padding: "0px 6px 0px 0px",
              ...(isItineraryMobile && {
                margin: "0px 0px 0px 0px",
                padding: "0px 0px 0px 0px",
              }),
            }}
          >
            {images?.map((item, index) => (
              <SwiperSlide key={index + 1} className="mobile_slider" data-swiper-autoplay="5000">
                {item.type == "image" ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      position: "relative",
                      ...(isItineraryMobile && {
                        img: {
                          borderRadius: "6px",
                          overflow: "hidden",
                        },
                      }),
                    }}
                  >
                    <Image
                      src={item?.href ?? ""}
                      alt={item?.name ?? ""}
                      fill
                      style={{ objectFit: "cover" }}
                      loader={({ src }) => src}
                      unoptimized
                      blurDataURL={item?.href ?? ""}
                      placeholder="blur"
                      priority
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      position: "relative",
                    }}
                  >
                    <ReactPlayer
                      className="react_player"
                      url={item?.href ?? ""}
                      width="100%"
                      height="100%"
                      config={{
                        youtube: {
                          playerVars: {
                            modestbranding: 1,
                            autoPlay: 1,
                            rel: 0,
                            fs: 0,
                          },
                        },
                      }}
                      controls={true}
                      playIcon={<></>}
                      muted
                      loop={true}
                      volume={0}
                    />
                  </Box>
                )}
              </SwiperSlide>
            ))}
            {/*  */}
          </Swiper>
          {(images?.length ?? 0) > imageLength && (
            <Button
              size="small"
              disableElevation
              variant="contained"
              aria-label="Next thumbnails"
              onClick={() => thumbsSwiper?.slideNext()}
              sx={{
                width: "25px",
                height: "50px",
                borderRadius: "6px",
                cursor: "pointer",
                zIndex: 1,
                minWidth: "unset",
                svg: {
                  m: { xs: "2px 2px", sm: "4px 4px" },
                  color: "#fff",
                  fontSize: { xs: "25px" },
                  rotate: "180deg",
                },
              }}
            >
              <ArrowLIcon />
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
